/**
 * @file Decides how to respond to a TokenEx iframe error, and when a replacement auth key
 * may reach the iframe.
 *
 */

import { isTokenExExpiryError } from "./tokenExError";

/** Attempts allowed per unproductive stretch, before the donor is shown the failure. */
export const MAX_EXPIRY_REFRESHES = 3;

/** Minimum gap between attempts. Re-asking immediately returns the same rejected key. */
export const EXPIRY_REFRESH_COOLDOWN_MS = 30 * 1000;

export type TokenExConfigApplication =
  /** Nothing to do (no config, or the one in hand is already applied) */
  | "none"
  /** A replacement is waiting, but applying it now would be unsafe. Re-evaluate later. */
  | "deferred"
  /** First config of the session. Wire it up without clearing anything. */
  | "initial"
  /** A newer key. Swap the iframe and clear the card state it just discarded. */
  | "replacement";

export interface TokenExConfigApplicationInput {
  /** The config the hook currently holds. */
  incoming: unknown;
  /** The config the iframe is currently built from, or null before the first. */
  applied: unknown;
  /** Checkout's form status: one of "ready", "loading", "success" or "error". */
  formStatus: string;
  /** Whether the card iframe currently has focus. */
  cardFocused?: boolean;
  /** Whether the key the iframe is currently built from is already past its TTL. */
  appliedKeyExpired?: boolean;
}

export const DEFERRED_APPLICATION_STATUSES = ["loading", "success"];

/**
 * Whether a config may reach the iframe yet. "deferred" rather than "none" is load-bearing:
 * the caller must re-evaluate when formStatus changes, or the config is lost until the next
 * refresh.
 */
export function decideConfigApplication({
  incoming,
  applied,
  formStatus,
  cardFocused = false,
  appliedKeyExpired = false
}: TokenExConfigApplicationInput): TokenExConfigApplication {
  if (!incoming || incoming === applied) {
    return "none";
  }

  if (DEFERRED_APPLICATION_STATUSES.includes(formStatus)) {
    return "deferred";
  }

  if (cardFocused && !appliedKeyExpired) {
    return "deferred";
  }

  return applied === null ? "initial" : "replacement";
}

export type TokenExRecoveryAction =
  /** Fetch a replacement auth key and rebuild the iframe. */
  | "refresh"
  /** Do nothing at all (another attempt already owns this failure) */
  | "ignore"
  /** Alert the donor. */
  | "surface";

export type TokenExFormOutcome = "busy" | "unchanged" | "ready";

export interface TokenExRecoveryDecision {
  action: TokenExRecoveryAction;
  form: TokenExFormOutcome;
  /** Info on why this decision was reached for logs */
  reason:
    | "not_expiry"
    | "refresh_in_flight"
    | "budget_exhausted"
    | "within_cooldown"
    | "recoverable";
}

export interface TokenExRecoveryInput {
  message: unknown;
  /** Whether a refresh kicked off by an earlier error has yet to settle. */
  refreshInFlight: boolean;
  attemptsUsed: number;
  msSinceLastAttempt: number;
  lastRefreshSucceeded?: boolean;
}

export function decideTokenExErrorAction({
  message,
  refreshInFlight,
  attemptsUsed,
  msSinceLastAttempt,
  lastRefreshSucceeded = false
}: TokenExRecoveryInput): TokenExRecoveryDecision {
  if (!isTokenExExpiryError(message)) {
    return { action: "surface", form: "ready", reason: "not_expiry" };
  }

  if (refreshInFlight) {
    return { action: "ignore", form: "unchanged", reason: "refresh_in_flight" };
  }

  if (attemptsUsed >= MAX_EXPIRY_REFRESHES) {
    return { action: "surface", form: "ready", reason: "budget_exhausted" };
  }

  if (msSinceLastAttempt < EXPIRY_REFRESH_COOLDOWN_MS) {
    return lastRefreshSucceeded
      ? { action: "ignore", form: "ready", reason: "within_cooldown" }
      : { action: "surface", form: "ready", reason: "within_cooldown" };
  }

  return { action: "refresh", form: "busy", reason: "recoverable" };
}
